import { useAuth } from '../../users/AuthContext';

/**
 * Custom hook for making authenticated API requests
 * Uses the AuthContext for proper authentication handling
 */
export const useAuthenticatedAPI = () => {
  const { makeAuthenticatedRequest } = useAuth();

  return {
    makeAuthenticatedRequest
  };
};

/**
 * API utility functions that work with AuthContext
 * These functions should be used with the useAuth hook
 */
export const createAPIUtilities = (makeAuthenticatedRequest) => {
  const userAPI = {
    // List all users with optional pagination
    listUsers: async (page = 1, limit = 20) => {
      try {
        const url = `http://localhost:8000/api/v1/users?page=${page}&limit=${limit}`;
        const data = await makeAuthenticatedRequest(url);
        
        return {
          success: true,
          data: data.data || [],
          message: data.message || 'Users retrieved successfully'
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || 'Failed to fetch users',
          data: []
        };
      }
    },

    // Create a new user
    createUser: async (userData) => {
      try {
        const data = await makeAuthenticatedRequest('http://localhost:8000/api/v1/users', {
          method: 'POST',
          body: JSON.stringify(userData)
        });
        
        return {
          success: true,
          data: data.data || {},
          message: data.message || 'User created successfully'
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || 'Failed to create user'
        };
      }
    },

    // Search user by email
    searchUserByEmail: async (email) => {
      try {
        const data = await makeAuthenticatedRequest(`http://localhost:8000/api/v1/users/search/email/${encodeURIComponent(email)}`);
        
        return {
          success: true,
          data: data.data || null,
          message: data.message || 'User search completed'
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || 'Failed to search user by email'
        };
      }
    },

    // Search user by ID
    searchUserById: async (id) => {
      try {
        const data = await makeAuthenticatedRequest(`http://localhost:8000/api/v1/users/search/id/${id}`);
        
        return {
          success: true,
          data: data.data || null,
          message: data.message || 'User search completed'
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || 'Failed to search user by ID'
        };
      }
    },

    // Search user by name
    searchUserByName: async (name) => {
      try {
        const data = await makeAuthenticatedRequest(`http://localhost:8000/api/v1/users/search/name/${encodeURIComponent(name)}`);
        
        return {
          success: true,
          data: data.data || [],
          message: data.message || 'User search completed'
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || 'Failed to search user by name'
        };
      }
    },    // Add user to group
    addUserToGroup: async (groupName, userId) => {
      try {
        const data = await makeAuthenticatedRequest(`http://localhost:8000/api/v1/groups/${groupName}/members`, {
          method: 'POST',
          body: JSON.stringify({ userUID: userId })
        });
        
        return {
          success: true,
          data: data.data || {},
          message: data.message || 'User added to group successfully'
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || 'Failed to add user to group'
        };
      }
    }
  };

  const groupAPI = {    // List all groups
    listGroups: async () => {
      try {
        const data = await makeAuthenticatedRequest('http://localhost:8000/api/v1/groups');
        
        return {
          success: true,
          data: data?.groups || data?.data || [], // Handle both response formats
          message: data?.message || 'Groups retrieved successfully'
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || 'Failed to fetch groups',
          data: []
        };
      }
    },

    // Create a new group
    createGroup: async (groupData) => {
      try {
        const data = await makeAuthenticatedRequest('http://localhost:8000/api/v1/groups', {
          method: 'POST',
          body: JSON.stringify({ groupName: groupData.cn, description: groupData.description })
        });
        
        return {
          success: true,
          data: data.data || {},
          message: data.message || 'Group created successfully'
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || 'Failed to create group'
        };
      }
    },

    // Search group by name
    searchGroupByName: async (name) => {
      try {
        const data = await makeAuthenticatedRequest(`http://localhost:8000/api/v1/groups/search/${encodeURIComponent(name)}`);
        
        return {
          success: true,
          data: data || [],
          message: 'Group search completed'
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || 'Failed to search group by name'
        };
      }
    },

    // Add subgroup to parent group
    addSubgroup: async (parentGroupName, subgroupName) => {
      try {
        const data = await makeAuthenticatedRequest(`http://localhost:8000/api/v1/groups/${parentGroupName}/subgroups`, {
          method: 'POST',
          body: JSON.stringify({ subGroupName: subgroupName })
        });
        
        return {
          success: true,
          data: data.data || {},
          message: data.message || 'Subgroup added successfully'
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || 'Failed to add subgroup'
        };
      }
    },    // Remove user from group
    removeUserFromGroup: async (groupName, userId) => {
      try {
        const data = await makeAuthenticatedRequest(`http://localhost:8000/api/v1/groups/${groupName}/members`, {
          method: 'DELETE',
          body: JSON.stringify({ userUID: userId })
        });
        
        return {
          success: true,
          data: data.data || {},
          message: data.message || 'User removed from group successfully'
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || 'Failed to remove user from group'
        };
      }
    }
  };

  return { userAPI, groupAPI };
};
